import fnsFormat from 'date-fns/format';
import { PromotionStatus } from '../types.es.js';
import { t } from '../i18n.es.js';

const formatPrice = (price) => {
    return price ? price.toLocaleString('vi-VN') : price;
};
const formatPriceFull = (price) => {
    return price || price === 0
        ? price.toLocaleString('vi-VN', {
            style: 'currency',
            currency: 'VND',
        })
        : price;
};
const sortItems = (childItems) => {
    return childItems.sort((left, right) => left.displayPriority > right.displayPriority ? 1 : -1);
};
const getComboName = (childItems) => {
    if (childItems.length === 0)
        return '';
    const primary = sortItems(childItems)[0];
    const comboLength = childItems.length;
    return comboLength === 1
        ? primary.name
        : t('{{PrimaryName}} và {{var0}} sản phẩm khác', {
            PrimaryName: primary.name,
            var0: comboLength - 1,
        });
};
const getComboImageUrl = (childItems) => {
    if (childItems.length === 0)
        return [];
    return sortItems(childItems).map((item) => { var _a, _b; return item.imageUrl || ((_b = (_a = item.product) === null || _a === void 0 ? void 0 : _a.productInfo) === null || _b === void 0 ? void 0 : _b.imageUrl); });
};
const getDateStringWithFormat = (dateString, format = 'HH:mm dd/MM/yyyy') => {
    try {
        return fnsFormat(new Date(dateString), format);
    }
    catch (_a) {
        return '';
    }
};
const getDateStringsTimeRange = (startDateString, endDateString) => {
    let str = '';
    const startDate = new Date(startDateString);
    const endDate = new Date(endDateString);
    if (startDate.getFullYear() !== endDate.getFullYear()) {
        str += `${getDateStringWithFormat(startDateString, 'dd/MM/yyyy')}`;
    }
    else {
        if (startDate.getMonth() !== endDate.getMonth()) {
            str += `${getDateStringWithFormat(startDateString, 'dd/MM')}`;
        }
        else {
            str += `${getDateStringWithFormat(startDateString, 'dd')}`;
        }
    }
    str += ` - ${getDateStringWithFormat(endDateString, 'dd/MM/yyyy')}`;
    return str;
};
const getBlockDescriptionForCartPromo = (promo, withTime = false) => {
    var _a, _b, _c;
    const condition = ((_a = promo === null || promo === void 0 ? void 0 : promo.basePromotion) === null || _a === void 0 ? void 0 : _a.condition) || (promo === null || promo === void 0 ? void 0 : promo.condition);
    if (!condition)
        return '';
    const { orderValueMin, orderValueMax } = condition;
    const minValue = formatPriceFull(orderValueMin);
    const maxValue = formatPriceFull(orderValueMax);
    const startedAt = (_b = promo === null || promo === void 0 ? void 0 : promo.basePromotion) === null || _b === void 0 ? void 0 : _b.startedAt;
    const endedAt = (_c = promo === null || promo === void 0 ? void 0 : promo.basePromotion) === null || _c === void 0 ? void 0 : _c.endedAt;
    let str = '';
    if (orderValueMin && orderValueMax) {
        str = `Áp dụng cho đơn hàng từ ${minValue} đến ${maxValue} `;
    }
    else if (orderValueMin) {
        str = `Áp dụng cho đơn hàng từ ${minValue} `;
    }
    else if (orderValueMax) {
        str = `Áp dụng cho đơn hàng tối đa ${maxValue} `;
    }
    else
        str = 'Áp dụng cho mọi đơn hàng';
    if (withTime) {
        str += ` . Áp dụng từ ${getDateStringsTimeRange(startedAt, endedAt)}`;
    }
    return str;
};
var PromoItemType;
(function (PromoItemType) {
    PromoItemType["GIFT"] = "gift";
    PromoItemType["DISCOUNT"] = "discount";
    PromoItemType["VOUCHER"] = "voucher";
})(PromoItemType || (PromoItemType = {}));
const getDiscountName = (discount) => {
    if (discount.flat) {
        return `Giảm ngay ${formatPriceFull(discount.flat)} vào giá trị đơn hàng`;
    }
    if (discount.percent) {
        const suffix = discount.maxAmountPerOrder
            ? ` (giảm tối đa trên đơn ${formatPriceFull(discount.maxAmountPerOrder)})`
            : '';
        return (`Giảm ngay ${discount.percent}% tối đa ${formatPriceFull(discount.maxAmount)}` + suffix);
    }
    return '';
};
const getVoucherName = (voucher) => {
    const defaultVoucherName = 'Tặng Voucher dùng cho đơn hàng sau';
    return !!voucher.name && !!voucher.quantity
        ? `${voucher.quantity} x ${voucher.name}`
        : defaultVoucherName;
};
const getGiftName = (gift) => {
    return `Quà tặng: ${gift.quantity} x ${gift.name} `;
};
const getBenefits = (benefit, withDiscount = true, condition) => {
    var _a;
    const results = [];
    if (benefit) {
        if (benefit.discount && withDiscount) {
            let flat = ((_a = benefit === null || benefit === void 0 ? void 0 : benefit.discount) === null || _a === void 0 ? void 0 : _a.flat) || 0;
            const blockSizeDecimal = condition === null || condition === void 0 ? void 0 : condition.blockSizeDecimal;
            if (blockSizeDecimal > 0 && blockSizeDecimal < 1) {
                flat = flat / blockSizeDecimal;
            }
            results.push({
                type: PromoItemType.DISCOUNT,
                text: getDiscountName(Object.assign(Object.assign({}, benefit.discount), { flat })),
            });
        }
        ((benefit === null || benefit === void 0 ? void 0 : benefit.gifts) || []).forEach((gift) => {
            const imgUrl = gift.imageUrl ? gift.imageUrl : '';
            results.push({
                type: PromoItemType.GIFT,
                text: getGiftName(gift),
                imgSrc: imgUrl,
            });
        });
        if (benefit.voucher) {
            results.push({
                type: PromoItemType.VOUCHER,
                text: getVoucherName(benefit.voucher),
            });
        }
    }
    return results;
};
const getPromotionProduct = (promotion) => {
    var _a, _b, _c, _d;
    const { benefit, condition } = promotion === null || promotion === void 0 ? void 0 : promotion.basePromotion;
    const gifts = promotion === null || promotion === void 0 ? void 0 : promotion.gifts;
    const results = [];
    if (benefit === null || benefit === void 0 ? void 0 : benefit.discount) {
        if ((_a = benefit === null || benefit === void 0 ? void 0 : benefit.discount) === null || _a === void 0 ? void 0 : _a.percent) {
            results.push({
                type: PromoItemType.DISCOUNT,
                text: t('Giảm {{percent}}% tối đa {{maxAmount}}', {
                    percent: (_b = benefit === null || benefit === void 0 ? void 0 : benefit.discount) === null || _b === void 0 ? void 0 : _b.percent,
                    maxAmount: formatPriceFull((_c = benefit === null || benefit === void 0 ? void 0 : benefit.discount) === null || _c === void 0 ? void 0 : _c.maxAmount),
                }),
            });
        }
        else {
            let flat = ((_d = benefit === null || benefit === void 0 ? void 0 : benefit.discount) === null || _d === void 0 ? void 0 : _d.flat) || 0;
            const blockSizeDecimal = condition === null || condition === void 0 ? void 0 : condition.blockSizeDecimal;
            if (blockSizeDecimal > 0 && blockSizeDecimal < 1) {
                flat = flat / blockSizeDecimal;
            }
            results.push({
                type: PromoItemType.DISCOUNT,
                text: t('Giảm tiền trực tiếp {{flat}}', {
                    flat: formatPriceFull(flat),
                }),
            });
        }
    }
    if (!!(gifts === null || gifts === void 0 ? void 0 : gifts.length)) {
        gifts === null || gifts === void 0 ? void 0 : gifts.forEach((gift) => {
            results.push({
                type: PromoItemType.GIFT,
                text: `${(gift === null || gift === void 0 ? void 0 : gift.quantityDecimal) || 0}x ${(gift === null || gift === void 0 ? void 0 : gift.name) || ''}`,
            });
        });
    }
    if (!!(benefit === null || benefit === void 0 ? void 0 : benefit.voucher)) {
        results.push({
            type: PromoItemType.VOUCHER,
            text: t('{{quantity}}x {{name}} Mã giảm giá cho đơn hàng sau', {
                quantity: benefit.voucher.quantity,
                name: benefit.voucher.name,
            }),
        });
    }
    return results;
};
const getVoucherBenefit = (maxAmount, percent, flat, orderValueMin, orderValueMax, applyOn) => {
    let benefit = '';
    if (flat) {
        benefit += `${t('Giảm')} ${formatPriceFull(flat)}`;
    }
    else if (percent) {
        benefit += `${t('Giảm')} ${percent}%`;
        if (maxAmount) {
            benefit += ` ${t('tối đa {{maxAmount}}', {
                maxAmount: formatPriceFull(maxAmount),
            })}`;
        }
    }
    else {
        benefit += 'Dành';
    }
    if (orderValueMin && orderValueMax) {
        benefit += ` ${t('cho đơn hàng từ {{min}} đến {{max}}', {
            min: formatPriceFull(orderValueMin),
            max: formatPriceFull(orderValueMax),
        })}`;
    }
    else if (orderValueMin) {
        benefit += ` ${t('cho đơn hàng tối thiểu {{min}}', {
            min: formatPriceFull(orderValueMin),
        })}`;
    }
    else if (orderValueMax) {
        benefit += ` ${t('cho đơn hàng dưới {{max}}', {
            max: formatPriceFull(orderValueMax),
        })}`;
    }
    else {
        benefit += ` ${t('cho tất cả các đơn hàng')}`;
    }
    if (applyOn === 'part_order') {
        benefit += ` (${t('Chỉ áp dụng trên một số sản phẩm')})`;
    }
    return benefit;
};
const getPromotionStatus = (cartPromotionId, isSelectable) => {
    if (cartPromotionId === null && isSelectable) {
        return PromotionStatus.UNSELECTED;
    }
    else if (cartPromotionId === null && !isSelectable) {
        return PromotionStatus.DISABLED;
    }
    else if (cartPromotionId !== null && isSelectable) {
        return PromotionStatus.SELECTED;
    }
    else {
        return PromotionStatus.FIXED;
    }
};

export { PromoItemType, formatPrice, formatPriceFull, getBenefits, getBlockDescriptionForCartPromo, getComboImageUrl, getComboName, getDateStringWithFormat, getDateStringsTimeRange, getPromotionProduct, getPromotionStatus, getVoucherBenefit };
